import { useState } from "react";
import { Link } from "react-router-dom";

const quickLinks = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Products & Services", path: "/products" },
  { label: "Gallery", path: "/gallery" },
  { label: "Testimonials", path: "/testimonials" },
  { label: "Contact Us", path: "/contact" },
];

const services = [
  { label: "Aluminium Bifold Doors", path: "/bifold-doors" },
  { label: "uPVC Windows", path: "/windows" },
  { label: "Aluminium Windows", path: "/windows#specs" },
  { label: "Composite Doors", path: "/composite-doors" },
  { label: "Conservatories", path: "/conservatories" },
  { label: "Roof Lanterns", path: "/roof-lanterns" },
  { label: "French Doors", path: "/products#french-doors" },
  { label: "Patio Doors", path: "/products#patio-doors" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch("https://readdy.ai/api/form/d720pp4991ove783t8cg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubscribed(true);
    } catch {
      setSubscribed(true);
    }
    setSubmitting(false);
  };

  return (
    <footer className="bg-[#1C2B1F] text-white">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img
              src="https://static.readdy.ai/image/d9440b223e6d80ac031633973d3203ba/a4e24dd15d398814f0f972e0887c314a.png"
              alt="Ensure Windows"
              className="h-14 w-auto object-contain mb-4"
            />
            <p className="text-stone-300 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Premium windows and doors for homes across Surrey and South London. Quality craftsmanship, trusted installations, lasting results.
            </p>
            <div className="flex gap-3 mt-5">
              {["ri-facebook-fill", "ri-instagram-line", "ri-twitter-x-line"].map((icon) => (
                <a key={icon} href="#" rel="nofollow" className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-amber-600 rounded-full transition-colors cursor-pointer">
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-amber-400 mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <a href="#quick-links">Quick Links</a>
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-stone-300 hover:text-amber-400 text-sm transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-amber-400 mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <a href="#our-services">Our Services</a>
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.label}>
                  <Link to={s.path} className="text-stone-300 hover:text-amber-400 text-sm transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-amber-400 mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <a href="#contact-info">Get in Touch</a>
            </h4>
            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5"><i className="ri-map-pin-2-line text-amber-400"></i></div>
                <span className="text-stone-300 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  Seven House, Town End<br />Caterham CR3 5UG
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line text-amber-400"></i></div>
                <a href="tel:07852636300" className="text-stone-300 hover:text-amber-400 text-sm transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>07852 636 300</a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-amber-400"></i></div>
                <a href="mailto:sales@ensurewindows.co.uk" className="text-stone-300 hover:text-amber-400 text-sm transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>sales@ensurewindows.co.uk</a>
              </li>
            </ul>

            {/* Newsletter */}
            <div>
              <p className="text-xs text-stone-400 mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>Subscribe for updates & offers</p>
              {subscribed ? (
                <p className="text-amber-400 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>Thank you for subscribing!</p>
              ) : (
                <form onSubmit={handleSubscribe} data-readdy-form className="flex gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 text-white placeholder-stone-400 text-sm px-3 py-2 rounded-md focus:outline-none focus:border-amber-400 min-w-0"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? "..." : "Join"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row justify-between items-center gap-3 flex-wrap">
          <p className="text-stone-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>
            &copy; 2026 Ensure Windows. All rights reserved. | 71-75 Shelton Street, London WC2H 9JQ
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <Link to="/privacy-policy" className="text-stone-400 hover:text-amber-400 text-xs transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>Privacy Policy</Link>
            <Link to="/terms-conditions" className="text-stone-400 hover:text-amber-400 text-xs transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>Terms &amp; Conditions</Link>
            <Link to="/cancellation-policy" className="text-stone-400 hover:text-amber-400 text-xs transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>Cancellation Policy</Link>
            <Link to="/cookies" className="text-stone-400 hover:text-amber-400 text-xs transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>Cookie Policy</Link>
            <span className="text-stone-500 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>Registered in England &amp; Wales</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
